import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * One fold-up list on the Lite Sites page (Jim 2026-10-06: Overdue, Rain
 * Event, My Sites, Group Sites in that order, "all of those collapsible", the
 * count kept in the heading so a folded list still says how many). The fold
 * sticks per device; a section with nothing in it starts folded.
 */
export function LiteListSection({
  id,
  title,
  count,
  icon,
  tone = 'default',
  intro,
  children,
}: {
  id: string;
  title: string;
  count: number;
  icon?: ReactNode;
  tone?: 'default' | 'danger' | 'rain';
  intro?: ReactNode;
  children: ReactNode;
}) {
  const storageKey = `lite.sites.${id}.open`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) return saved === '1';
    } catch {
      /* private mode: no memory of the fold */
    }
    return count > 0;
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };
  const header =
    tone === 'rain'
      ? 'bg-sky-600 text-white'
      : tone === 'danger'
        ? 'bg-danger-50 dark:bg-danger-900/20 text-danger-800 dark:text-danger-200'
        : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100';
  return (
    <section id={id} className="scroll-mt-20 space-y-3">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`${id}-list`}
        className={`w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-left ${header}`}
      >
        {icon}
        <h2 className="flex-1 text-sm font-semibold">
          {title} ({count})
        </h2>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div id={`${id}-list`} className="space-y-3">
          {intro}
          {children}
        </div>
      )}
    </section>
  );
}
